import { createRouter, createWebHistory } from 'vue-router'
import { useCurrentUser } from '@/composables/useCurrentUser'
import { homeRouteFor } from '@/utils/homeRoute'
import { pageTitleFor } from '@/utils/pageTitle'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // No-auth layout sandbox, dev-only (stripped from production builds).
    ...(import.meta.env.DEV ? [{
      path: '/preview-dispatch',
      name: 'preview-dispatch',
      component: () => import('@/views/PreviewDispatchView.vue'),
    }] : []),
    {
      path: '/',
      component: () => import('@/components/layout/AppLayout.vue'),
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('@/views/DashboardView.vue'),
          meta: { requiresAdmin: true, featureKey: 'dashboard' },
        },
        {
          path: 'deals',
          name: 'deals',
          component: () => import('@/views/DealsView.vue'),
          meta: { requiresAdmin: true, featureKey: 'deals' },
          children: [
            {
              path: ':id',
              name: 'deal-detail',
              component: () => import('@/views/DealsView.vue'),
              meta: { requiresAdmin: true, featureKey: 'deals' },
            },
          ],
        },
        {
          path: 'leads',
          name: 'leads',
          component: () => import('@/views/LeadsView.vue'),
          meta: { requiresAdmin: true, featureKey: 'leads' },
          children: [
            {
              path: ':id',
              name: 'lead-detail',
              component: () => import('@/views/LeadsView.vue'),
              meta: { requiresAdmin: true, featureKey: 'leads' },
            },
          ],
        },
        {
          // Conversations (Textline replacement). The selected thread is a child route that
          // renders the same view, so a thread link can be pasted and opens in place.
          // Keep the meta identical to navigation.js's `conversations` entry.
          path: 'conversations',
          name: 'conversations',
          component: () => import('@/views/ConversationsView.vue'),
          meta: { requiresSmsLines: true },
          children: [
            {
              path: ':id',
              name: 'conversation',
              component: () => import('@/views/ConversationsView.vue'),
              meta: { requiresSmsLines: true },
            },
          ],
        },
        {
          // Universal deal viewer for the system-wide unique-id search. No role gate:
          // any authenticated user can open a lead they looked up (server opens the
          // row read; field-level permissions still govern what they see/edit).
          path: 'deal/:id',
          name: 'deal-view',
          component: () => import('@/views/DealViewerView.vue'),
        },
        {
          path: 'buyers',
          name: 'buyers',
          component: () => import('@/views/BuyersView.vue'),
          meta: { requiresAdmin: true, featureKey: 'buyers' },
        },
        {
          path: 'auction-yards',
          name: 'auction-yards',
          component: () => import('@/views/AuctionYardsView.vue'),
          meta: { requiresAdmin: true, featureKey: 'auction_yards' },
        },
        {
          path: 'schedule-pickup',
          name: 'schedule-pickup',
          component: () => import('@/views/SchedulePickupView.vue'),
          meta: { requiresRole: 'dispatch', featureKey: 'schedule_pickup' },
        },
        {
          path: 'dispatch',
          name: 'dispatch',
          component: () => import('@/views/DispatchView.vue'),
          // Intake reps live in this view too (courtesy queue + pool chips) — without
          // 'intake' here they bounced off the guard into the hold-calculator fallback
          // and the app looked permissionless.
          //
          // title / title_supervisor added 2026-08-06: the Title Work tab is now the title
          // team's main surface, and it only exists inside this view. Today's title reps
          // (Cristina 134, Stephany 128) happen to hold role=manager and passed on
          // canViewAll, so the hole only showed on a correctly-onboarded pure title rep.
          // Keep this list in step with navigation.js's `dispatch` entry.
          meta: { requiresRole: ['dispatch', 'intake', 'title', 'title_supervisor'], featureKey: 'dispatch' },
          children: [
            {
              path: ':id',
              name: 'dispatch-detail',
              component: () => import('@/views/DispatchView.vue'),
              meta: { requiresRole: ['dispatch', 'intake', 'title', 'title_supervisor'], featureKey: 'dispatch' },
            },
          ],
        },
        {
          // Title Team board: notary/title/USPS/cashier's-check assignments in flight.
          // Title reps + title supervisors (managers/admins pass via canViewAll).
          path: 'title-queue',
          name: 'title-queue',
          component: () => import('@/views/TitleTeamQueueView.vue'),
          meta: { requiresRole: ['title', 'title_supervisor'], featureKey: 'title_queue' },
        },
        {
          // Email/RV rep lead queue. The lead opens INSIDE this view (a child route, not
          // a modal or a separate page) because the workspace is messaging-first and the
          // rep is meant to work the thread with the queue still beside them — Julian,
          // 0813: "a very high percentage of our leads get closed via text message."
          // Every meta below must stay identical to its NAV_CONFIG access rule.
          path: 'email-rv',
          name: 'emailrv-queue',
          component: () => import('@/views/EmailRvQueueView.vue'),
          meta: { requiresRole: ['email_rep', 'quoter', 'email_manager'], featureKey: 'emailrv_queue' },
          children: [
            {
              path: 'lead/:id',
              name: 'emailrv-lead',
              component: () => import('@/views/EmailRvQueueView.vue'),
              meta: { requiresRole: ['email_rep', 'quoter', 'email_manager'], featureKey: 'emailrv_queue' },
            },
          ],
        },
        {
          // The quoters' board. "The most important feature" of the quoter view (0813).
          path: 'email-rv/requests',
          name: 'emailrv-requests',
          component: () => import('@/views/EmailRvRequestsView.vue'),
          meta: { requiresRole: ['quoter', 'email_manager'], featureKey: 'emailrv_requests' },
        },
        {
          // Reviewer routing (Julian 9/11, Quoter 1): the private review queue. Gated on
          // carrying a reviewer label, not on a role — Sam's plain-rep login must see it.
          path: 'email-rv/reviews',
          name: 'emailrv-reviews',
          component: () => import('@/views/EmailRvReviewsView.vue'),
          meta: { requiresReviewer: true },
        },
        {
          // The digital Parcel. Manager-only, manual assignment only — the APM-value /
          // self-serve model discussed on 0813 is a proposal, not a decision.
          path: 'email-rv/distribution',
          name: 'emailrv-distribution',
          component: () => import('@/views/EmailRvDistributionView.vue'),
          meta: { requiresRole: ['email_manager'], featureKey: 'emailrv_distribution' },
        },
        {
          // Bought leads waiting on a manager's check before submission (Julian 10/9).
          // Managers and Julian only.
          path: 'email-rv/submission-review',
          name: 'emailrv-submission-review',
          component: () => import('@/views/EmailRvSubmissionReviewView.vue'),
          meta: { requiresRole: ['email_manager'], featureKey: 'emailrv_submission_review' },
        },
        {
          // Reps who pressed "Get New Assignment" and are still empty-handed (Julian, 9/19).
          path: 'email-rv/waiting',
          name: 'emailrv-waiting',
          component: () => import('@/views/EmailRvWaitingView.vue'),
          meta: { requiresRole: ['email_manager'], featureKey: 'emailrv_waiting' },
        },
        {
          // Manager KPIs (spec §4.3/§4.7): queue health, per-rep workloads, automation.
          path: 'email-rv/dashboard',
          name: 'emailrv-dashboard',
          component: () => import('@/views/EmailRvDashboardView.vue'),
          meta: { requiresRole: ['email_manager', 'quoter'], featureKey: 'emailrv_dashboard' },
        },
        {
          // Hidden from the sidebar 2026-09-24 (Angel 091926, navigation.js `hidden`). The
          // route stays so the page still opens from a saved link and comes back by
          // un-hiding one nav entry; nothing in the app links here any more.
          path: 'courtesy-assignment',
          name: 'courtesy-assignment',
          component: () => import('@/views/CourtesyAssignmentView.vue'),
          meta: { requiresRole: 'dispatch_admin', featureKey: 'courtesy_assignment' },
        },
        {
          // Employee Coaching Logs (Angel 102226): every signed-in user reads and signs their
          // own here, so no role meta. The API only returns a log to its employee or to QA.
          path: 'coaching/:id?',
          name: 'coaching-logs',
          component: () => import('@/views/CoachingLogsView.vue'),
        },
        {
          path: 'manager-queue',
          name: 'manager-queue',
          component: () => import('@/views/ManagerQueueView.vue'),
          meta: { requiresRole: 'dispatch_admin', featureKey: 'manager_queue' },
          children: [
            {
              path: ':id',
              name: 'manager-queue-detail',
              component: () => import('@/views/ManagerQueueView.vue'),
              meta: { requiresRole: 'dispatch_admin', featureKey: 'manager_queue' },
            },
          ],
        },
        {
          // Read-only supervisor view of ONE rep's Work Queue. Optional :userId so the
          // nav item can land on an empty picker. NOTE: the backend endpoint is
          // role-gated only (requireRole has no feature-flag path), so granting the
          // agent_view flag alone shows the page and then 403s on load.
          path: 'agent-view/:userId?',
          name: 'agent-view',
          component: () => import('@/views/AgentQueueView.vue'),
          meta: { requiresRole: ['dispatch_admin'], featureKey: 'agent_view' },
        },
        {
          path: 'dispatch-admin',
          component: () => import('@/views/DispatchAdminView.vue'),
          meta: { requiresRole: 'dispatch_admin', featureKey: 'dispatch_admin' },
          children: [
            { path: '', redirect: '/dispatch-admin/skill-matrix' },
            {
              path: 'skill-matrix',
              name: 'dispatch-admin-skill-matrix',
              component: () => import('@/components/dispatch/admin/SkillMatrixEditor.vue'),
            },
            {
              path: 'field-permissions',
              name: 'dispatch-admin-field-permissions',
              component: () => import('@/components/dispatch/admin/FieldPermissionMatrix.vue'),
            },
            {
              path: 'buckets',
              name: 'dispatch-admin-buckets',
              component: () => import('@/components/dispatch/admin/BucketsAdmin.vue'),
            },
            // Junk quoting moved to /tools/junk-quoting 2026-10-08; old links still land.
            { path: 'junk-quoting', redirect: '/tools/junk-quoting' },
            {
              path: 'settings',
              name: 'dispatch-admin-settings',
              component: () => import('@/components/dispatch/admin/DispatchSettings.vue'),
            },
            {
              path: 'work-time',
              name: 'dispatch-admin-work-time',
              component: () => import('@/components/dispatch/admin/WorkTimeReport.vue'),
              meta: { requiresRole: 'dispatch_admin' },
            },
            {
              path: 'productivity',
              name: 'dispatch-admin-productivity',
              component: () => import('@/components/dispatch/admin/ProductivityReport.vue'),
              meta: { requiresRole: ['dispatch_admin', 'title_supervisor'] },
            },
          ],
        },
        {
          // Accounting: every payment line across deals. Keep in step with NAV_CONFIG 'payments'.
          path: 'payments',
          name: 'payments',
          component: () => import('@/views/PaymentsView.vue'),
          meta: { requiresRole: ['payment_issuer', 'manager', 'dispatch_admin'], featureKey: 'payments' },
        },
        {
          path: 'hold-calculator',
          name: 'hold-calculator',
          component: () => import('@/views/HoldCalculatorView.vue'),
        },
        {
          // Preview-locked to JR and Samy. MUST stay identical to the quote_calculator entry
          // in config/navigation.js; the backend refuses everyone else regardless.
          path: 'quote-calculator',
          name: 'quote-calculator',
          component: () => import('@/views/QuoteCalculatorView.vue'),
          meta: { requiresQuoteCalculatorPreview: true },
        },
        // Daily tools brought into the CRM (meeting #11). Julian: integrated beats a
        // bookmark. No role gate — any rep who escalates needs this.
        {
          path: 'tools/escalation',
          name: 'escalation-tool',
          component: () => import('@/views/EscalationToolView.vue'),
        },
        {
          // The yard script, rebuilt in the CRM. No role gate — dispatch, offers and
          // the title team all look buyers up.
          path: 'tools/buyer-search',
          name: 'buyer-search',
          component: () => import('@/views/BuyerSearchView.vue'),
        },
        {
          // Junk offer rules (margins, LA opening offer, required fields). Same roles as the
          // GET in backend routes/settings.js; managers pass via canViewAll. Quoters view only.
          // MUST stay identical to the junk_quoting entry in config/navigation.js.
          path: 'tools/junk-quoting',
          name: 'junk-quoting-settings',
          component: () => import('@/views/JunkQuotingSettingsView.vue'),
          meta: { requiresRole: ['dispatch_admin', 'email_manager', 'quoter'], featureKey: 'junk_quoting' },
        },
        {
          path: 'bulk-update',
          name: 'bulk-update',
          component: () => import('@/views/BulkUpdateView.vue'),
          meta: { requiresAdmin: true, featureKey: 'bulk_update' },
        },
        {
          path: 'title-requirements',
          name: 'title-requirements',
          component: () => import('@/views/TitleRequirementsView.vue'),
          meta: { requiresRole: ['title', 'title_supervisor', 'dispatch', 'dispatch_admin'], featureKey: 'title_requirements' },
        },
        {
          path: 'title-requirements/:stateCode',
          name: 'title-requirement-state',
          component: () => import('@/views/TitleRequirementStateView.vue'),
          meta: { requiresRole: ['title', 'title_supervisor', 'dispatch', 'dispatch_admin'], featureKey: 'title_requirements' },
        },
        {
          path: 'call-quality',
          name: 'call-quality',
          component: () => import('@/views/CallQualityView.vue'),
          meta: { requiresAdmin: true, featureKey: 'call_quality' },
        },
        {
          path: 'sales-bot-board',
          name: 'sales-bot-board',
          component: () => import('@/views/SalesBotBoardView.vue'),
          // Flag-only: the board is a standalone per-person permission with no role
          // bypass, so requiresFeature (not requiresRole + featureKey). See the
          // BOARD_FLAG note in backend/src/routes/sales-bot-board.js.
          meta: { requiresFeature: 'sales_bot_board' },
        },
        {
          path: 'watchdog',
          name: 'watchdog',
          component: () => import('@/views/WatchdogView.vue'),
          meta: { requiresWatchdogPreview: true },
        },
        {
          path: 'conversion-review',
          name: 'conversion-review',
          component: () => import('@/views/ConversionReviewView.vue'),
          meta: { requiresConversionReview: true },
        },
        {
          path: 'sales-bot-settings',
          name: 'sales-bot-settings',
          component: () => import('@/views/SalesBotSettingsView.vue'),
          meta: { requiresSuperAdmin: true },
        },
        {
          path: 'prompt-releases',
          name: 'prompt-releases',
          component: () => import('@/views/PromptReleasesView.vue'),
          meta: { requiresSuperAdmin: true },
        },
        {
          path: 'business-hours',
          name: 'business-hours',
          component: () => import('@/views/BusinessHoursView.vue'),
          meta: { requiresSuperAdmin: true },
        },
        {
          path: 'user-management',
          name: 'user-management',
          component: () => import('@/views/UserManagementView.vue'),
          meta: { requiresSuperAdmin: true },
        },
      ],
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue'),
    },
    {
      // Where Clerk invitations land (LoginView's signUpUrl). Missing until 2026-09-10:
      // SignIn forwarded every sign_up ticket here, the router matched nothing, and the
      // invitee saw a blank page. See SignUpView.vue.
      path: '/signup/:pathMatch(.*)*',
      name: 'signup',
      component: () => import('@/views/SignUpView.vue'),
    },
    {
      path: '/unauthorized',
      name: 'unauthorized',
      component: () => import('@/views/UnauthorizedView.vue'),
    },
    {
      // Catch-all. Until 2026-09-15 there was none, so ANY url the router did not match
      // rendered a permanent blank white page: App.vue is a bare <RouterView/>, and with
      // no matched record it renders nothing at all. No error, no console message, no
      // redirect — indistinguishable from a broken deploy, and it cost a real debugging
      // session. Stale bookmarks, a mistyped path and an old link in a chat message all
      // land here, so it must say something.
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/UnauthorizedView.vue'),
    },
  ],
  scrollBehavior() {
    return { top: 0 }
  },
})

// Route guard for role-restricted routes
router.beforeEach(async (to) => {
  // Clerk invitation tickets. The invite email lands on the site root carrying
  // `__clerk_ticket` (+ `__clerk_status=sign_up`). Before 2026-09-10 the guard below
  // bounced the signed-out visitor to their fallback page, which dropped the query, and
  // Clerk's SignedOut redirect then sent them to the hosted accounts.fcbcrm.com sign-in
  // with no ticket — "Couldn't find your account" for all five of Angel's testers.
  // Hand the ticket to the SignUp page before any role gate can eat it.
  if (to.query.__clerk_ticket && to.name !== 'signup' && to.name !== 'login') {
    return { path: '/signup', query: to.query }
  }

  const needsSuperAdmin = to.meta.requiresSuperAdmin
  const needsAdmin = to.meta.requiresAdmin
  const needsRole = to.meta.requiresRole
  const needsWatchdog = to.meta.requiresWatchdogPreview
  const needsConversionReview = to.meta.requiresConversionReview
  const needsQuoteCalculator = to.meta.requiresQuoteCalculatorPreview
  // Standalone per-person permission: the named feature flag is the ONLY way in —
  // no role, canViewAll, or super_admin bypass.
  const needsFeature = to.meta.requiresFeature
  const needsReviewer = to.meta.requiresReviewer
  // Conversations: anyone on an SMS line, plus managers/super_admins (who see every line).
  const needsSmsLines = to.meta.requiresSmsLines

  if (!needsSuperAdmin && !needsAdmin && !needsRole && !needsWatchdog && !needsConversionReview && !needsQuoteCalculator && !needsFeature && !needsReviewer && !needsSmsLines) return true

  const { canViewAll, isSuperAdmin, canViewWatchdog, canViewConversionReview, canViewQuoteCalculator, canViewConversations, featureFlags, hasRole, isReady, fetchCrmUser, crmUser, lastStatus } = useCurrentUser()

  // If CRM user hasn't been loaded yet (e.g. hard refresh), fetch it first
  if (!isReady.value) {
    await fetchCrmUser()
  }

  // Not signed in at all (the /me call got a 401): this is not a permissions question, and
  // judging roles on a nobody sent people to the "no access" page for a stale session
  // (JR, 2026-09-15 — a quoter's pasted lead link, opened the next morning). Let the route
  // render instead: AppLayout's <SignedOut><RedirectToSignIn/> then sends them to sign in
  // with THIS url as the return address, so they land on the link, not on a fallback page.
  // A 403 (signed in, no CRM row) still falls through to the role checks below.
  if (!crmUser.value && lastStatus.value === 401) return true

  // Two different things happen when access is refused, and conflating them was the bug.
  //
  //   LANDING ('/')  — nobody typed this. It is where login drops you, and '/' is
  //                    admin-only, so a title rep or an email_rep arrives without access
  //                    every single time. Silently forwarding them to their own home is
  //                    correct and must stay: an Unauthorized wall at login would be
  //                    alarming and wrong. See utils/homeRoute.js for the mapping.
  //
  //   ANYTHING ELSE  — somebody typed it, clicked it, or was sent it. Redirecting them
  //                    somewhere else without a word makes the app look broken ("I clicked
  //                    the link Angel sent and it just went to my queue"). Say so instead,
  //                    and carry the attempted path so the page can name it. This is the
  //                    change JR asked for on 2026-09-15, app-wide rather than per-route.
  const home = homeRouteFor(hasRole, canViewAll)
  const denied = to.path === '/'
    ? home
    : { name: 'unauthorized', query: { from: to.fullPath } }

  // Texting-only account (every role is `texter`): Conversations is the whole app. Login
  // lands there; anything else typed or linked gets the Unauthorized page like any other
  // role would, and the backend refuses the APIs regardless (restrictTexters.js).
  const roleList = crmUser.value?.roles || []
  if (roleList.length > 0 && roleList.every((r) => r === 'texter') && !to.meta.requiresSmsLines && to.name !== 'unauthorized' && to.name !== 'not-found') {
    return denied
  }

  if (!to.meta.requiresSuperAdmin && !to.meta.requiresWatchdogPreview && !to.meta.requiresConversionReview && !needsQuoteCalculator) {
    const featureKey = to.meta.featureKey
    if (featureKey && featureFlags.value.includes(featureKey)) return true
  }

  // Reviewer routing: a label is the grant; super_admin passes (sees every label).
  if (needsReviewer && !crmUser.value?.reviewerLabel && !isSuperAdmin.value) {
    return denied
  }

  if (needsSmsLines && !canViewConversations.value) {
    return denied
  }

  // Flag-only pages (Sales Bot Board): no role/super_admin bypass by design.
  if (needsFeature && !featureFlags.value.includes(needsFeature)) {
    return denied
  }

  // Ads Watchdog is preview-locked to specific super_admins (temporary)
  if (needsWatchdog && !canViewWatchdog.value) {
    return denied
  }

  // Conversion Review is restricted to the reviewer email allowlist (no role bypass)
  if (needsConversionReview && !canViewConversionReview.value) {
    return denied
  }

  // Quote Calculator is preview-locked to JR and Samy (email allowlist, no role bypass)
  if (needsQuoteCalculator && !canViewQuoteCalculator.value) {
    return denied
  }

  if (needsSuperAdmin && !isSuperAdmin.value) {
    return denied
  }

  if (needsAdmin && !canViewAll.value) {
    return denied
  }

  // Role-specific pages: check if user has the role (super_admin always passes via hasRole)
  const hasNeededRole = Array.isArray(needsRole) ? needsRole.some(r => hasRole(r)) : hasRole(needsRole)
  if (needsRole && !hasNeededRole && !canViewAll.value) {
    return denied
  }

  return true
})

// Tab + Chrome history title per page ("Deal 48976 · Family Car Buyers"). See utils/pageTitle.js.
router.afterEach((to) => {
  document.title = pageTitleFor(to)
})

export default router
